import React, { useEffect, useState } from 'react';
import { Card, CardContent, Typography, Box, ToggleButtonGroup, ToggleButton, CircularProgress, Alert, useTheme } from '@mui/material';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, BarChart, Bar, AreaChart, Area } from 'recharts';
import { apiClient } from 'utils/ApiClient';
import { Logger } from 'utils/Logger';
import { selectLabels } from 'state/dictionary/dictionarySlice';
import { useSelector } from 'react-redux';
import { RootState } from 'state/store';
import { getIuChartTooltipContentStyle } from './instantUpdateChartTheme';

type TimeRange = '7d' | '30d' | '90d';

interface Row {
  date: string;
  success: number;
  failed: number;
  skipped: number;
  pending: number;
}
interface Resp { timeRange: TimeRange; timeSeriesData: Row[] }

export const WhatsappCampaignSummaryGraph: React.FC = () => {
  const theme = useTheme();
  const [timeRange, setTimeRange] = useState<TimeRange>('7d');
  const [chartType, setChartType] = useState<'line' | 'bar' | 'area'>('area');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [rows, setRows] = useState<Row[]>([]);
  const labels = useSelector((state: RootState) => selectLabels(state));
  const fetchData = async () => {
    setLoading(true); setError(null);
    try {
      const resp = await apiClient.get(`/iu/analytics/whatsapp/summary?timeRange=${timeRange}`);
      const data = resp.data as Resp;
      setRows(data.timeSeriesData || []);
    } catch (e: any) {
      const msg = e?.message || 'Failed to load WhatsApp summary';
      setError(msg); Logger.error('WhatsappCampaignSummaryGraph', msg);
    } finally { setLoading(false); }
  };

  useEffect(() => { fetchData(); }, [timeRange]);

  const renderChart = () => {
    const tooltipStyle = getIuChartTooltipContentStyle(theme);
    const commonProps = { data: rows, margin: { top: 10, right: 20, left: 0, bottom: 0 } } as const;
    const base = (
      <>
        <CartesianGrid strokeDasharray="3 3" />
        <XAxis dataKey="date" />
        <YAxis allowDecimals={false} />
        <Tooltip contentStyle={tooltipStyle} />
        <Legend />
      </>
    );
    if (chartType === 'bar') {
      return (
        <BarChart {...commonProps}>
          {base}
          <Bar dataKey="success" fill={theme.palette.success.main} name="Success" />
          <Bar dataKey="failed" fill={theme.palette.error.main} name="Failed" />
        </BarChart>
      );
    }
    if (chartType === 'area') {
      return (
        <AreaChart {...commonProps}>
          {base}
          <Area type="monotone" dataKey="success" stroke={theme.palette.success.main} fill={theme.palette.success.light} name="Success" />
          <Area type="monotone" dataKey="failed" stroke={theme.palette.error.main} fill={theme.palette.error.light} name="Failed" />
        </AreaChart>
      );
    }
    return (
      <LineChart {...commonProps}>
        {base}
        <Line type="monotone" dataKey="success" stroke={theme.palette.success.main} dot={false} name="Success" />
        <Line type="monotone" dataKey="failed" stroke={theme.palette.error.main} dot={false} name="Failed" />
      </LineChart>
    );
  };

  return (
    <Card
      elevation={theme.palette.dashboard?.gaugePanelBackgroundColor ? 0 : 1}
      sx={{
        mb: 4,
        borderRadius: 3,
        ...(theme.palette.dashboard?.gaugePanelBackgroundColor
          ? {
              bgcolor: theme.palette.dashboard.gaugePanelBackgroundColor,
              boxShadow: theme.palette.dashboard.gaugePanelBoxShadow ?? 3,
            }
          : {
              bgcolor: (t: any) =>
                t.palette.mode === 'dark' ? 'rgb(20, 25, 55)' : t.palette.grey[100],
            }),
      }}
    >
      <CardContent sx={{ p: 3 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h6">{labels?.['modules-instantupdate-dashboard-whatsapp-campaign-summary-graph-title'] ?? 'modules-instantupdate-dashboard-whatsapp-campaign-summary-graph-title'}</Typography>
          <Box display="flex" gap={1}>
            <ToggleButtonGroup size="small" exclusive value={chartType} onChange={(_e, v) => v && setChartType(v)}>
              <ToggleButton value="line">Line</ToggleButton>
              <ToggleButton value="bar">Bar</ToggleButton>
              <ToggleButton value="area">Area</ToggleButton>
            </ToggleButtonGroup>
            <ToggleButtonGroup size="small" exclusive value={timeRange} onChange={(_e, v) => v && setTimeRange(v)}>
              <ToggleButton value="7d">7d</ToggleButton>
              <ToggleButton value="30d">30d</ToggleButton>
              <ToggleButton value="90d">90d</ToggleButton>
            </ToggleButtonGroup>
          </Box>
        </Box>
        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight={160}><CircularProgress /></Box>
        ) : error ? (
          <Alert severity="error">{error}</Alert>
        ) : rows.length === 0 ? (
          <Typography variant="body2" color="textSecondary">No data in selected range.</Typography>
        ) : (
          <Box height={320}>
            <ResponsiveContainer>
              {renderChart()}
            </ResponsiveContainer>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};


